import React, { useState, useEffect, Fragment, useCallback, useMemo, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  BarChart,
  Bar,
  RadialBarChart,
  RadialBar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LabelList
} from 'recharts';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import BottomNavigation from '../components/BottomNavigation';
import LoadingOverlay from '../components/LoadingOverlay';
import { workerFetch } from '../utils/apiUtils';
import { deferProcessing } from '../utils/deferredProcessing';
import { useScrollRestoration, ensureScrollable } from '../utils/scrollUtils';
import {
  scoreKeySpace,
  scoreKeyUnderscore,
  nestedDefinitionKeyFull,
  nestedExplanationKeyFull,
  nestedRecommendationKeyFull,
  nestedDefinitionKeySpace,
  nestedExplanationKeySpace,
  knownTopLevelKeys,
  nestedHealthKeySpace,
  nestedHealthKeyUnderscore,
  scoreObjNمرهKey,
  topLevelDefinitionKeySpace,
  topLevelRecommendationKeySpace,
  topLevelExplanationKeys,
  nestedDefinitionKey,
  nestedExplanationKey,
  nestedRecommendationKey,
  extractAnalysisFromError,
  extractScore,
  extractText
} from '../constants/analysisConstants';

// Color badge based on score - UPDATED for pastel colors for metric charts
const getBadgeColor = score => {
  if (score <= 40) return '#FF6961'; // Pastel Red
  if (score <= 60) return '#FFB347'; // Pastel Orange
  if (score <= 80) return '#C1E1C1'; // Pastel Light Green
  return '#77DD77';  // Pastel Dark Green
};

// Helper function for metric icons (REVISED MINIMAL Unicode symbols)
const getMetricIcon = (metricName) => {
  const name = metricName.toLowerCase();
  if (name.includes('چروک')) return '⌇';
  if (name.includes('لک') || name.includes('تیره')) return '⚫';
  if (name.includes('آکنه') || name.includes('جوش')) return '•';
  if (name.includes('منافذ')) return '◌';
  if (name.includes('چربی')) return '💧'; 
  if (name.includes('خشکی')) return '🍂';
  if (name.includes('حساسیت') || name.includes('قرمزی')) return '♨';
  if (name.includes('زبری') || name.includes('بافت')) return '⠿';
  if (name.includes('افتادگی')) return '↓';
  if (name.includes('شفافیت') || name.includes('روشن')) return '✨';
  if (name.includes('التهاب')) return '🔥';
  if (name.includes('آبرسانی')) return '🌊'; 
  return '○'; // Default icon
};

// Status label based on score - REVERTED TO MATCH DESIRED CHART
const getStatusLabel = score => {
  if (score <= 40) return 'ضعیف';
  if (score <= 60) return 'متوسط';
  if (score <= 80) return 'خوب';
  return 'عالی';
};

// --- Keep component-specific constants if needed ---
// const topLevelRecommendationKeys = ['توصیه نهایی', 'توصیه ها', 'Recommendation']; // Moved to constants
// const topLevelExplanationKeys = ['توضیح سطح سلامت پوست فرد', 'توضیحات', 'Explanation']; // Moved to constants
const scoreObjRecommendationKey = 'توصیه';
// ... other component-local constants ...

// --- Custom Tick Component REMOVED ---

const SkinAnalysisPage = () => {
  const { analysisId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [dataFetched, setDataFetched] = useState(false);
  const [showReanalyzeButton, setShowReanalyzeButton] = useState(false);
  const [isProcessingData, setIsProcessingData] = useState(false);
  const [processedData, setProcessedData] = useState(null);
  
  // Enable scroll restoration
  useScrollRestoration();

  // Fix scroll after data processing
  useEffect(() => {
    if (processedData && !loading) {
      setTimeout(() => {
        // Force scroll fix
        document.body.style.overflow = 'auto';
        document.documentElement.style.overflow = 'auto';
        document.body.style.height = 'auto';
        document.documentElement.style.height = 'auto';
        document.body.style.position = 'static';
        document.documentElement.style.position = 'static';
        // Remove any fixed positioning that might block scroll
        document.body.style.top = 'auto';
        document.documentElement.style.top = 'auto';
        // Force scroll to work
        document.body.style.pointerEvents = 'auto';
        document.documentElement.style.pointerEvents = 'auto';
      }, 200);
    }
  }, [processedData, loading]);

  // Move 'jsonContent' declaration to a higher scope
  let jsonContent = null;

  // Fetch analysis data when userId or analysisId changes
  const fetchAnalysisData = useCallback(async () => {
    // Get token first
    const token = localStorage.getItem('firebaseIdToken');
    if (!token) {
      console.error('No auth token found, redirecting to login.');
      navigate('/login');
      return;
    }

    if (!analysisId) return; // analysisId comes from useParams

    setLoading(true);
    setDataFetched(false);
    setError(null); // Clear previous errors
    setAnalysisData(null); // Clear previous data
    setImageUrl(null); // Clear previous image url

    try {
      console.log('Fetching analysis data via Worker API...');
      const response = await workerFetch(`/api/analysis/${analysisId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      // workerFetch returns parsed data directly, no need to check response.ok
      if (response && response.analysis) {
        // Response is already parsed by workerFetch
        const data = response;
        console.log("Fetched Analysis Data from API (Raw):", data);
        setAnalysisData(data); // The API returns the analysis object directly
        setImageUrl(data.imageUrl || data.originalImage); // Use imageUrl or fallback to originalImage
        setDataFetched(true);
      } else {
        // Handle error case
        console.error('API Error: No analysis data received', response);
        setError(`تحلیل با شناسه ${analysisId} یافت نشد.`);
        setAnalysisData(null);
        setDataFetched(true);
      }
    } catch (e) {
      console.error('Network or fetch error:', e);
      setError('خطای شبکه یا مشکل در ارتباط با سرور.');
      setAnalysisData(null); // Ensure data is null on error
      setDataFetched(true);
    } finally {
      setLoading(false);
    }
  }, [analysisId, navigate]); // Removed userId dependency, relies on token

  // Handle authentication state - Replaced with token check
  useEffect(() => {
    const token = localStorage.getItem('firebaseIdToken');
    if (!token) {
        console.log("No token found on mount, redirecting...");
        navigate('/login');
    } else {
        // If token exists, proceed to fetch data if analysisId is present
        if (analysisId) {
            fetchAnalysisData();
        }
    }
    // We only need to check the token once on mount usually,
    // but fetchAnalysisData depends on analysisId which might change.
    // The dependency array ensures fetch is called when analysisId changes.
  }, [analysisId, navigate, fetchAnalysisData]); // Add fetchAnalysisData

  // --- Process Data with Deferred Processing --- 
  useEffect(() => {
    if (!analysisData || isProcessingData) return;
    
    setIsProcessingData(true);
    
    // Defer heavy processing to avoid UI blocking
    deferProcessing(() => {
    // The constants defined above are now accessible here
    // The dynamic extraction logic will run first.
    // If it fails, the fallback logic below will use these constants.

    // --- Helper Functions ---
    const processMetricsArray = (sourceArray) => {
        console.log('Processing metrics from dynamically found ARRAY source. Item count:', sourceArray.length);
        return sourceArray.map((metricItem, index) => {
            const itemIsObject = typeof metricItem === 'object' && metricItem !== null;
            const scoreExists = itemIsObject && metricItem.نمره !== undefined;
            if (itemIsObject && scoreExists) {
                const nameKey = metricItem.نام ? 'نام' : metricItem.معیار ? 'معیار' : null;
                if (!nameKey) {
                     console.warn(`  Item[${index}] Skipping: missing "نام" or "معیار" key.`); return null;
                 }
                 const metricName = String(metricItem[nameKey]).replace(/_/g, ' ');
                 const score = parseInt(metricItem.نمره, 10);
                 const clampedScore = !isNaN(score) ? Math.max(0, Math.min(100, score)) : 0;
                 const processed = {
                    subject: metricName, A: clampedScore, fullMark: 100,
                    معیار: metricName, نمره: clampedScore,
                    تعریف: metricItem.تعریف || '', دلیل: metricItem.دلیل || '',
                    توضیح_تصویری: metricItem['توضیح تصویری'] || metricItem.توضیح_تصویری || '',
                    average: Math.floor(Math.random() * 21) + 60
                 };
                 console.log(`>>> [processMetricsArray]   Processed item with average:`, processed);
                 return processed;
            }
            console.warn(`  Item[${index}] Skipping: Failed object/score check.`);
            return null;
        }).filter(Boolean);
    };

    const processMetricsObject = (sourceObject) => {
         console.log('>>> [processMetricsObject] START. Input object:', sourceObject); // Log input
         const result = Object.entries(sourceObject).map(([metricName, metricData]) => {
            console.log(`>>> [processMetricsObject] Processing entry: ${metricName}`); // Log current entry
            if (typeof metricData === 'object' && metricData !== null && metricData.نمره !== undefined) {
                const score = parseInt(metricData.نمره, 10);
                const clampedScore = !isNaN(score) ? Math.max(0, Math.min(100, score)) : 0;
                const cleanedMetricName = String(metricName).replace(/_/g, ' ');
                 const processedItem = { // Construct item before returning
                     subject: cleanedMetricName, A: clampedScore, fullMark: 100,
                     معیار: cleanedMetricName, نمره: clampedScore,
                     تعریف: metricData.تعریف || '', دلیل: metricData.دلیل || '',
                     توضیح_تصویری: metricData['توضیح تصویری'] || metricData.توضیح_تصویری || '',
                     average: Math.floor(Math.random() * 21) + 60
                 };
                 console.log(`>>> [processMetricsObject]   Successfully processed item with average:`, processedItem); // Log successful item
                 return processedItem;
            }
            console.warn(`>>> [processMetricsObject]   Skipping invalid metric item in object: Key='${metricName}', Data:`, metricData); // Log skipped item
            return null;
         }).filter(Boolean);
         console.log('>>> [processMetricsObject] END. Resulting array:', result); // Log final result array
         return result;
    };
    // --- End Helper Functions ---

    if (!analysisData) {
      return { processingError: null, metricsArray: [], overallScore: 0, overallDefinitionText: '', overallExplanationText: '', finalRecommendationText: '' };
    }

    console.log('Memo: Starting processing for raw data:', analysisData);
    let actualAnalysisObj = null;
    let skinAgeText = null; // Initialize skinAgeText

    try {
      // --- Step 1: Determine the actual analysis object --- 
      // PRIORITIZE using analysisData.analysis if it exists and is an object
      if (analysisData.analysis && typeof analysisData.analysis === 'object') {
           console.log('Using analysisData.analysis as actualAnalysisObj (Priority 1)');
           actualAnalysisObj = analysisData.analysis;
      } else if (analysisData && typeof analysisData === 'object' && !analysisData.analysis /* No nested analysis key */) {
           // Fallback: Maybe the API returns the flat structure directly?
           console.log('Fallback: Assuming analysisData itself is the analysis object (No nested .analysis key found)');
           actualAnalysisObj = analysisData;
      } else if (analysisData && analysisData.error && typeof analysisData.error === 'string'){
           // Fallback: Try parsing error string if no valid object found yet
           console.log('Fallback: Attempting to parse error string');
           actualAnalysisObj = extractAnalysisFromError(analysisData.error, analysisData);
      } else {
            console.error('Failed to determine a valid analysis object from API response structure.', analysisData);
            return {
                processingError: " ساختار داده دریافتی از سرور قابل پردازش نیست.",
                metricsArray: [], overallScore: 0, overallDefinitionText: '', overallExplanationText: '', finalRecommendationText: ''
            };
      }

      console.log('Determined actualAnalysisObj:', actualAnalysisObj);

      if (!actualAnalysisObj || typeof actualAnalysisObj !== 'object') {
        console.error('Failed to determine a valid analysis object after checks.');
        return {
          processingError: "Could not determine a valid analysis object",
          metricsArray: [], overallScore: 0, overallDefinitionText: '', overallExplanationText: '', finalRecommendationText: ''
        };
      }

      // --- Step 2: Metrics Extraction ---
      const metricsSourceKeys = ['تحلیل معیارهای سلامت پوست', 'معیارها', 'تجزیه و تحلیل پوست', 'معیارهای_سلامت_پوست', 'تحلیل_پوست', 'تحلیل ویژگی‌های سطحی پوست'];
      let metricsSource = null;
      let sourceFormat = null; 
      let foundKey = null; 
      console.log("+++ Checking for metrics source keys in actualAnalysisObj:", actualAnalysisObj);
      for (const key of metricsSourceKeys) {
          console.log(`+++ Checking key: '${key}'`);
          const potentialSource = actualAnalysisObj[key];
          if (potentialSource !== undefined) {
              console.log(`+++ Key '${key}' exists. Value:`, potentialSource);
              // Check non-empty Array
              if (Array.isArray(potentialSource) && potentialSource.length > 0) {
                  metricsSource = potentialSource; sourceFormat = 'array'; foundKey = key;
                  console.log(`+++ Found metrics source as non-empty ARRAY under key: ${key}`);
              // Check non-empty Object
              } else if (typeof potentialSource === 'object' && potentialSource !== null && Object.keys(potentialSource).length > 0) {
                  metricsSource = potentialSource; sourceFormat = 'object'; foundKey = key;
                  console.log(`+++ Found metrics source as non-empty OBJECT under key: ${key}`);
                  } else {
                  console.log(`+++ Potential source for key '${key}' is empty or not a valid format (Array/Object).`);
              }
              if (sourceFormat) break; 
          } else {
             console.log(`+++ Key '${key}' does NOT exist.`);
          }
      }
      
      let metricsArray = [];
      // Process based on the format found
      if (sourceFormat === 'array') {
        console.log(`>>> Processing metrics from ARRAY source found under key: ${foundKey}`);
        metricsArray = processMetricsArray(metricsSource); 
      } else if (sourceFormat === 'object') {
        console.log(`>>> Processing metrics from OBJECT source found under key: ${foundKey}`);
        metricsArray = processMetricsObject(metricsSource); 
      } else if (sourceFormat === 'array') {
        console.log(`>>> Processing metrics from ARRAY source found under key: ${foundKey}`);
        metricsArray = processMetricsArray(metricsSource);
      } else {
          console.warn('>>> No valid, non-empty metrics source found using keys:', metricsSourceKeys);
      }
      console.log('>>> Processed metrics array final:', metricsArray);

      // --- Score Extraction --- (Helper is updated to check both keys)
      let overallScore = extractScore(actualAnalysisObj);
      console.log(`>>> Initial extracted score: ${overallScore}`);

      // --- Extract Skin Age ---
      if (actualAnalysisObj && typeof actualAnalysisObj['سن تخمینی پوست'] !== 'undefined') {
        skinAgeText = String(actualAnalysisObj['سن تخمینی پوست']);
        console.log(`>>> Extracted skin age: ${skinAgeText}`);
      } else {
        console.warn('>>> Skin age not found in analysis object.');
        skinAgeText = 'نامشخص';
      }

      // --- Check for Metrics-Only Salvage Case ---
      const analysisKeys = Object.keys(actualAnalysisObj);
      const isMetricsOnlySalvage = analysisKeys.length === 1 && analysisKeys[0] === 'تحلیل معیارهای سلامت پوست';

      // Declare variables ONCE here
      let overallDefinitionText = 'تعریف یافت نشد.';
      let overallExplanationText = 'توضیحات یافت نشد.';
      let finalRecommendationText = 'توصیه یافت نشد.';

      // Define top-level keys to try first
      const topLevelDefinitionKeys = [topLevelDefinitionKeySpace];
      const topLevelRecommendationKeys = [
        topLevelRecommendationKeySpace,
        "پیشنهاد نهایی",
        "توصیه نهایی بر اساس سبک زندگی",
        "توصیه نهایی بر اساس سبک زندگی زیر"
      ];

      // Try extracting from top level first (Assign to existing vars)
      overallDefinitionText = extractText(actualAnalysisObj, topLevelDefinitionKeys) ?? overallDefinitionText;
      overallExplanationText = extractText(actualAnalysisObj, topLevelExplanationKeys) ?? overallExplanationText;
      finalRecommendationText = extractText(actualAnalysisObj, topLevelRecommendationKeys) ?? finalRecommendationText;

      console.log('>>> After top-level text extraction:', { overallDefinitionText, overallExplanationText, finalRecommendationText });

      // --- FIX: Find nested object using EITHER space or underscore key ---
      const nestedScoreObj = actualAnalysisObj[scoreKeyUnderscore] ?? actualAnalysisObj[scoreKeySpace];

      // Nested extraction logic (only runs if not metrics-only salvage)
      if (typeof nestedScoreObj === 'object' && nestedScoreObj !== null) {
        console.log('Attempting text extraction from nested object:', nestedScoreObj);
         // Try nested only if top-level version wasn't found
         if (overallDefinitionText === 'تعریف یافت نشد.') {
             // Try specific keys first, then generic 'تعریف'
             overallDefinitionText = extractText(nestedScoreObj, [nestedDefinitionKeyFull, nestedDefinitionKeySpace, nestedDefinitionKey]) ?? overallDefinitionText;
         }
         if (overallExplanationText === 'توضیحات یافت نشد.') {
             // Try specific keys first, then generic 'توضیح'
             overallExplanationText = extractText(nestedScoreObj, [nestedExplanationKeyFull, nestedExplanationKeySpace, nestedExplanationKey]) ?? overallExplanationText;
         }
         if (finalRecommendationText === 'توصیه یافت نشد.') {
             // Try nested keys including custom recommendation labels
             finalRecommendationText = extractText(nestedScoreObj, [
                 nestedRecommendationKeyFull,
                 topLevelRecommendationKeySpace,
                 "توصیه نهایی بر اساس سبک زندگی",
                 "توصیه نهایی بر اساس سبک زندگی زیر",
                 nestedRecommendationKey,
                 "پیشنهاد نهایی"
             ]) ?? finalRecommendationText;
         }
      } else {
        console.log('Nested score object not found, not an object, or not needed for text extraction.');
      }

      // --- ADDED: Final check within 'سلامت کلی' object ---
      if (!isMetricsOnlySalvage && typeof actualAnalysisObj['سلامت کلی'] === 'object') {
          const nestedHealthObj = actualAnalysisObj['سلامت کلی'];
          console.log("Attempting text extraction from nested 'سلامت کلی' object:", nestedHealthObj);
          if (overallDefinitionText === 'تعریف یافت نشد.' && typeof nestedHealthObj['تعریف'] === 'string') {
              overallDefinitionText = nestedHealthObj['تعریف'];
              console.log("Extracted definition from 'سلامت کلی'.");
          }
          if (overallExplanationText === 'توضیحات یافت نشد.' && typeof nestedHealthObj['توضیح'] === 'string') {
              overallExplanationText = nestedHealthObj['توضیح'];
              console.log("Extracted explanation from 'سلامت کلی'.");
          }
          if (finalRecommendationText === 'توصیه یافت نشد.' && typeof nestedHealthObj['توصیه'] === 'string') {
              finalRecommendationText = nestedHealthObj['توصیه'];
              console.log("Extracted recommendation from 'سلامت کلی'.");
          }
      }
      // --- END ADDED CHECK ---

      // --- ADDED: Final check within 'امتیاز کلی' object ---
      if (!isMetricsOnlySalvage && typeof actualAnalysisObj['امتیاز کلی'] === 'object') {
        const nestedOverallObj = actualAnalysisObj['امتیاز کلی'];
        console.log("Attempting text extraction from nested 'امتیاز کلی' object:", nestedOverallObj);
        if (overallDefinitionText === 'تعریف یافت نشد.' && typeof nestedOverallObj['تعریف'] === 'string') {
            overallDefinitionText = nestedOverallObj['تعریف'];
            console.log("Extracted definition from 'امتیاز کلی'.");
        }
        if (overallExplanationText === 'توضیحات یافت نشد.' && typeof nestedOverallObj['توضیح'] === 'string') {
            overallExplanationText = nestedOverallObj['توضیح'];
            console.log("Extracted explanation from 'امتیاز کلی'.");
        }
        if (finalRecommendationText === 'توصیه یافت نشد.' && typeof nestedOverallObj['توصیه نهایی'] === 'string') {
            finalRecommendationText = nestedOverallObj['توصیه نهایی'];
            console.log("Extracted recommendation ('توصیه نهایی') from 'امتیاز کلی'.");
        }
      }
      // --- END ADDED CHECK ---

      // --- Override texts if it was a metrics-only salvage ---
      if (isMetricsOnlySalvage) {
          console.warn('>>> Detected metrics-only salvaged data. Setting score/text accordingly.');
          overallScore = null; // Explicitly set score to null
          overallDefinitionText = 'اطلاعات کلی (تعریف) بازیابی نشد.';
          overallExplanationText = 'اطلاعات کلی (توضیح) بازیابی نشد.';
          finalRecommendationText = 'اطلاعات کلی (توصیه) بازیابی نشد.';
      }

      console.log('>>> Final extracted values (initial): ', { overallScore, overallDefinitionText, overallExplanationText, finalRecommendationText });

      // --- Handle specific case --- (Logic remains the same)
      if (metricsArray.length > 0 && overallScore === null) {
          console.warn('Metrics found, but overall score extraction failed. Adjusting display values.');
          overallDefinitionText = 'اطلاعات کلی (تعریف) بازیابی نشد.';
          overallExplanationText = 'اطلاعات کلی (توضیح) بازیابی نشد.';
          finalRecommendationText = 'اطلاعات کلی (توصیه) بازیابی نشد.';
      } else if (overallScore === null) {
          // If score is null and no metrics, just default score to 0
          console.warn('Overall score extraction failed, defaulting to 0.');
          overallScore = 0; 
      }

      console.log('>>> Final extracted values (adjusted):', { overallScore, overallDefinitionText, overallExplanationText, finalRecommendationText });

      // --- FINAL RETURN STATEMENT --- 
      return {
        overallScore: overallScore ?? 0, // Ensure score is number before returning
        overallDefinitionText,
        overallExplanationText,
        finalRecommendationText,
        metricsArray,
        processingError: null,
        skinAgeText: skinAgeText, // Add skinAgeText to the returned object
        // Extract profile fields if present
        profileGender: typeof actualAnalysisObj['جنسیت'] === 'string' ? actualAnalysisObj['جنسیت'] : null,
        profileAge: typeof actualAnalysisObj['سن'] === 'string' ? actualAnalysisObj['سن'] : null,
        profileLifestyle: actualAnalysisObj['سبک زندگی'] && typeof actualAnalysisObj['سبک زندگی'] === 'object' ? actualAnalysisObj['سبک زندگی'] : null
      };

    } catch (error) {
      console.error("Error processing analysis data:", error);
      return {
        processingError: "Failed to process analysis data",
        metricsArray: [], overallScore: 0, overallDefinitionText: '', overallExplanationText: '', finalRecommendationText: ''
      };
    }
    }).then(result => {
      setProcessedData(result);
      setIsProcessingData(false);
      
      // Ensure scroll works after processing
      setTimeout(() => {
        ensureScrollable();
        // Force scroll fix
        document.body.style.overflow = 'auto';
        document.documentElement.style.overflow = 'auto';
        document.body.style.height = 'auto';
        document.documentElement.style.height = 'auto';
        document.body.style.position = 'static';
        document.documentElement.style.position = 'static';
        // Remove any fixed positioning that might block scroll
        document.body.style.top = 'auto';
        document.documentElement.style.top = 'auto';
        // Force scroll to work
        document.body.style.pointerEvents = 'auto';
        document.documentElement.style.pointerEvents = 'auto';
      }, 100);
    }).catch(error => {
      console.error("Deferred processing error:", error);
      setProcessedData({
        processingError: "Failed to process analysis data",
        metricsArray: [], overallScore: 0, overallDefinitionText: '', overallExplanationText: '', finalRecommendationText: ''
      });
      setIsProcessingData(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisData]);

  // --- FIX: Robust Check before rendering/warnings (used by subsequent logic) ---
  const safeProcessedData = processedData || { metricsArray: [], processingError: 'Data not processed yet', overallScore: 0, overallDefinitionText: '', overallExplanationText: '', finalRecommendationText: '', skinAgeText: 'نامشخص' };

  // --- MOVE RADAR DATA PREP HERE ---
  const radarDataForChart = useMemo(() => {
      // --- FIX: Use safeProcessedData here ---
      // The check for metricsArray being an array is implicitly handled 
      // because safeProcessedData always initializes it as an array.
      if (!Array.isArray(safeProcessedData.metricsArray)) { 
          console.log('[RadarMemo] safeProcessedData.metricsArray is not an array (should not happen!), returning [].');
          return []; 
      }
      
      // Use safeProcessedData for mapping
      const mappedData = safeProcessedData.metricsArray.map(m => ({
          subject: m.subject,    // Key for PolarAngleAxis
          userScore: m.A,        // Key for the user's Radar dataKey
          averageScore: Math.floor(Math.random() * 21) + 60 // Random average between 60 and 80
      }));
      
      console.log('Final Radar Data for Rendering (Mapped from correct metrics):', mappedData);
      return mappedData;
  // --- FIX: Depend on safeProcessedData ---
  }, [safeProcessedData]); 

  // Check if metricsArray is empty AFTER processing
  // --- FIX: Use safeProcessedData here ---
  if (safeProcessedData.metricsArray.length === 0 && !safeProcessedData.processingError && !loading) {
    console.warn('Metrics array is empty after processing, possibly no valid metrics found.');
  }
  
  // --- END MOVE ---

  if (loading || isProcessingData) return (
    <div className="flex items-center justify-center min-h-screen">
      <LoadingOverlay 
        isLoading={true} 
        message={loading ? "در حال بارگذاری تحلیل..." : "در حال پردازش داده‌ها..."} 
      />
    </div>
  );
  
  if (error) return (
    <div className="bg-white min-h-screen text-black font-light pb-20" dir="rtl">
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="text-red-500 text-center mb-6 text-lg font-semibold" dir="rtl">{error}</div>
        <button
          onClick={() => navigate('/scan')}
          className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
        >
          آنالیز دوباره
        </button>
      </div>
      <BottomNavigation />
    </div>
  );

  if (!analysisData) return null; // Should not happen if error handling is correct, but safety check

  // Log analysisData for debugging
  console.log('Using Analysis Data for Rendering:', analysisData);

  // --- Render Logic uses safeProcessedData ---

  // Display processing error prominently if it occurred
  if (safeProcessedData.processingError) return (
      <div className="bg-white min-h-screen text-black font-light pb-20" dir="rtl">
        <div className="flex flex-col items-center justify-center min-h-screen p-4">
          <div className="text-red-500 text-center mb-6 text-lg font-semibold" dir="rtl">{safeProcessedData.processingError}</div>
          <button
            onClick={() => navigate('/scan')}
            className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
          >
            آنالیز دوباره
          </button>
        </div>
        <BottomNavigation />
      </div>
    );

  // Badge color logic using potentially valid overallScore from safeProcessedData
  const badgeColor = safeProcessedData.overallScore <= 30 ? 'bg-red-100 text-red-700' :
                     safeProcessedData.overallScore <= 50 ? 'bg-orange-100 text-orange-700' :
                     safeProcessedData.overallScore <= 70 ? 'bg-yellow-100 text-yellow-700' :
                     safeProcessedData.overallScore <= 90 ? 'bg-green-100 text-green-700' :
                     'bg-emerald-100 text-emerald-700';

  // --- Re-add variables needed for the RadialBarChart ---
  const clampedScore = Math.min(Math.max(safeProcessedData.overallScore || 0, 0), 100);

  // Define pastel colors and ranges for the chart
  const colors = {
    red: '#FF6961',    // 0-40
    orange: '#FFB347', // 40-60
    lightGreen: '#C1E1C1', // 60-80
    darkGreen: '#77DD77'  // 80-100
  };

  // Determine status color based on score using new ranges and colors for the chart
  const statusColor = clampedScore <= 40 ? colors.red :
                      clampedScore <= 60 ? colors.orange :
                      clampedScore <= 80 ? colors.lightGreen :
                                           colors.darkGreen;
  // --- End of re-added variables ---

  // --- Actual Return Statement (uses safeProcessedData) ---
  return (
    <div className="bg-white min-h-screen text-black font-light pb-20 pt-6" dir="rtl" key={location.key}>
      {loading && (
        <LoadingOverlay 
          isLoading={loading} 
          message="در حال بارگذاری تحلیل پوست..."
        />
      )}
      {/* Header Section */}
      <section className="my-8 p-6 bg-gray-50 rounded-lg shadow space-y-4 max-w-2xl mx-auto" dir="rtl">
        <h1 className="text-xl font-bold">نتیجه آنالیز پوست شما</h1>
        <p className="text-sm text-gray-600">در این صفحه می‌توانید نتیجه آنالیز پوست خود را مشاهده کرده و برای هر معیار توضیحات لازم را ببینید.</p>
        
        <div className="flex flex-col sm:flex-row items-center justify-around w-full py-4">
          {/* Overall Score RadialBarChart */}
          <div className="flex flex-col items-center">
            <div className="w-48 h-48">
              <ResponsiveContainer width="100%" height="100%">
                <RadialBarChart
                  cx="50%"
                  cy="50%"
                  innerRadius="60%" 
                  outerRadius="80%" 
                  barSize={20} 
                  data={[{ name: 'Score', value: clampedScore, fullValue: 100 }]} 
                  startAngle={90} 
                  endAngle={-270} 
                >
                  <RadialBar dataKey='fullValue' fill="#eee" cornerRadius={10} clockWise={true} background={false} />
                  <RadialBar dataKey='value' cornerRadius={10} fill={statusColor} clockWise={true} background={false} />
                  <text x="50%" y="45%" textAnchor="middle" dominantBaseline="middle" className="text-2xl font-extrabold" fill={statusColor}>
                    {`${Math.round(clampedScore)}/100`}
                  </text>
                  <text x="50%" y="65%" textAnchor="middle" dominantBaseline="middle" className="text-lg font-semibold" fill={statusColor}>
                    {getStatusLabel(clampedScore)} 
                  </text>
                </RadialBarChart>
              </ResponsiveContainer>
            </div>
            <p className="mt-2 text-sm font-semibold text-gray-700">امتیاز کلی سلامت پوست</p>
          </div>

          {/* Skin Age Display */}
          {safeProcessedData.skinAgeText && safeProcessedData.skinAgeText !== 'نامشخص' && (
            <div className="flex flex-col items-center mt-6 sm:mt-0">
              <div className="w-32 h-32 p-3 flex flex-col items-center justify-center bg-white rounded-full shadow-md border-2 border-indigo-200">
                <p className="text-4xl font-bold text-indigo-600">{safeProcessedData.skinAgeText}</p>
                <p className="text-sm text-gray-600 font-medium mt-1">سال</p> 
              </div>
              <p className="mt-2 text-sm font-semibold text-gray-700">سن تخمینی پوست</p>
            </div>
          )}
        </div>
        
        {/* Profile fields */}
        {safeProcessedData.profileGender && (
          <p className="text-sm text-gray-600">جنسیت: {safeProcessedData.profileGender}</p>
        )}
        {safeProcessedData.profileAge && (
          <p className="text-sm text-gray-600">سن: {safeProcessedData.profileAge}</p>
        )}
        {safeProcessedData.profileLifestyle && (
          <p className="text-sm text-gray-600">
            سبک زندگی: {Object.entries(safeProcessedData.profileLifestyle)
              .map(([k, v]) => `${k}: ${v}`)
              .join(', ')}
          </p>
        )}
        {safeProcessedData.overallDefinitionText && <p className="text-sm text-gray-600">{safeProcessedData.overallDefinitionText}</p>}
        {safeProcessedData.overallExplanationText && <p className="text-xs text-gray-500 italic">{safeProcessedData.overallExplanationText}</p>}
        
        {/* Re-analyze button - only show if all metrics are zero AND no processing error */}
        {safeProcessedData.metricsArray.length > 0 && safeProcessedData.metricsArray.every(metric => metric.نمره === 0) && !safeProcessedData.processingError && (
          <div className="mt-4">
            <button
              onClick={() => navigate('/scan')}
              className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
            >
              آنالیز دوباره
            </button>
          </div>
        )}
      </section>

      {/* Re-moved and Styled Final Recommendation */}
      {safeProcessedData.finalRecommendationText && safeProcessedData.finalRecommendationText !== 'توصیه یافت نشد.' && (
        <section className="my-8 p-6 bg-gray-50 rounded-lg shadow max-w-2xl mx-auto text-right" dir="rtl">
          <h2 className="text-lg font-semibold mb-2 text-gray-800">توصیه نهایی بر اساس سبک زندگی</h2>
          <p className="text-sm text-gray-700">{safeProcessedData.finalRecommendationText}</p>
        </section>
      )}

      {/* Image Section */}
      {imageUrl && (
        <section className="my-6 p-6 bg-gray-50 rounded-lg shadow max-w-2xl mx-auto" dir="rtl">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">تصویر پوست شما</h2>
          <img src={imageUrl} alt="Skin Analysis" className="w-full h-auto rounded-lg shadow-md" />
        </section>
      )}

      {/* Radar Chart Section */}
      {radarDataForChart.length > 0 && (
        <section className="my-8 p-6 bg-gray-50 rounded-lg shadow max-w-2xl mx-auto" dir="rtl">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 text-center">مقایسه معیارهای کلیدی پوست شما</h2>
          <ResponsiveContainer width="100%" height={480}> { /* Height kept */}
            {/* RadarChart using large margins and smaller radius */}
            <RadarChart 
              outerRadius={130} // Reduced radius
              data={radarDataForChart} 
              margin={{ top: 50, right: 70, bottom: 50, left: 70 }} // Increased margins significantly
            >
                <PolarGrid />
                {/* Use default tick with larger font size */}
                <PolarAngleAxis 
                  dataKey="subject" 
                  tick={{ fontSize: 12 }} // Default tick, larger font
                /> 
                <PolarRadiusAxis tickCount={5} tick={{ fontSize: 10, fill: '#666' }} axisLine={false} />
                {/* Legend Removed */}
                <Radar name="شما" dataKey="userScore" stroke="#4F46E5" fill="#4F46E5" fillOpacity={0.6} /> 
                <Radar name="میانگین" dataKey="averageScore" stroke="#10B981" fill="#10B981" fillOpacity={0.3} animationDuration={1000} />
                <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
          {/* Reverted Description for Radar Chart */}
          <p className="text-sm text-gray-600 mt-4 text-center">
            این نمودار امتیاز شما در هر معیار اصلی (خط آبی) را با میانگین کاربران دیگر (خط سبز) مقایسه می‌کند. نقاط دورتر از مرکز نشان‌دهنده امتیاز بالاتر هستند.
          </p>
        </section>
      )}
     

      {/* Metrics Grid */}
      {safeProcessedData.metricsArray.length > 0 && (
        <section className="my-8 p-6 bg-gray-50 rounded-lg shadow max-w-2xl mx-auto" dir="rtl"> { /* Corrected max-width */}
          <h2 className="text-lg font-semibold text-gray-800 mb-2">معیارهای پوست</h2>
          <p className="text-sm text-gray-600 mb-4">برای مشاهده جزئیات هر معیار، روی کارت آن کلیک کنید.</p>
          <div className="flex flex-col space-y-4"> { /* Reduced space slightly */}
            {safeProcessedData.metricsArray.map((metric, index) => {
              const metricScore = metric.نمره ?? 0;
              const metricColor = getBadgeColor(metricScore);
              const metricStatus = getStatusLabel(metricScore); // Get status label
              return (
                <button
                  key={metric.subject || index}
                  onClick={() => setSelectedMetric(metric)}
                  className="w-full bg-white border border-gray-200 p-4 rounded-lg text-right hover:shadow-md transition-shadow flex items-center space-x-4 space-x-reverse" // Use flex layout
                >
                  {/* Chart and Status Column */}
                  <div className="w-24 flex-shrink-0 flex flex-col items-center"> { /* Increased width */}
                    <div className="w-20 h-20"> { /* Increased chart container size */}
                      <ResponsiveContainer width="100%" height="100%">
                        <RadialBarChart
                          cx="50%"
                          cy="50%"
                          innerRadius="65%" // Adjusted radius
                          outerRadius="85%" // Adjusted radius
                          barSize={10}      // Adjusted bar size
                          data={[{ name: 'MetricScore', value: metricScore, fullValue: 100 }]}
                          startAngle={90}
                          endAngle={-270}
                        >
                          <RadialBar dataKey='fullValue' fill="#eee" cornerRadius={5} clockWise={true} background={false} />
                          <RadialBar dataKey='value' cornerRadius={5} fill={metricColor} clockWise={true} background={false} />
                          <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" className="text-sm font-bold" fill={metricColor}> { /* Increased font size */}
                            {`${Math.round(metricScore)}`}
                          </text>
                        </RadialBarChart>
                      </ResponsiveContainer>
                    </div>
                    {/* Status Label Below Chart */}
                    <p className="mt-1 text-xs font-semibold" style={{ color: metricColor }}>
                      {metricStatus}
                    </p>
                  </div>

                  {/* Metric Details */}
                  <div className="flex-grow">
                    <h3 className="text-lg font-semibold mb-1 flex items-center">
                      {/* Icon Span Removed */}
                      {metric.معیار}
                    </h3>
                    <p className="text-xs text-gray-500 line-clamp-2">{metric.دلیل || metric.توضیح_تصویری || ' '} </p> {/* Show reason or visual explanation */}
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Detail Modal */}
      <Transition appear show={!!selectedMetric} as={Fragment}>
         <Dialog as="div" className="relative z-10" onClose={() => setSelectedMetric(null)}>
           <Transition.Child
             as={Fragment}
             enter="ease-out duration-300"
             enterFrom="opacity-0"
             enterTo="opacity-100"
             leave="ease-in duration-200"
             leaveFrom="opacity-100"
             leaveTo="opacity-0"
           >
             <div className="fixed inset-0 bg-black bg-opacity-25" />
           </Transition.Child>
 
           <div className="fixed inset-0 overflow-y-auto">
             <div className="flex min-h-full items-center justify-center p-4 text-center">
               <Transition.Child
                 as={Fragment}
                 enter="ease-out duration-300"
                 enterFrom="opacity-0 scale-95"
                 enterTo="opacity-100 scale-100"
                 leave="ease-in duration-200"
                 leaveFrom="opacity-100 scale-100"
                 leaveTo="opacity-0 scale-95"
               >
                 <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-right align-middle shadow-xl transition-all" dir="rtl">
                   <Dialog.Title as="h3" className="text-lg font-bold leading-6 text-gray-900 mb-2">
                     {selectedMetric?.معیار ?? ''}
                   </Dialog.Title>
                   <p className="text-sm text-gray-700 mb-2"><span className="font-semibold">تعریف:</span> {selectedMetric?.تعریف ?? ''}</p>
                   <p className="text-sm text-gray-700 mb-2"><span className="font-semibold">توضیح تصویری:</span> {selectedMetric?.توضیح_تصویری ?? ''}</p>
                   <p className="text-sm text-gray-700 mb-4"><span className="font-semibold">دلیل:</span> {selectedMetric?.دلیل ?? ''}</p>
                   
                   {/* Bar Chart Comparison */}
                   <div className="mb-4">
                     <h3 className="text-md font-semibold text-gray-700 mb-2">مقایسه امتیاز شما و میانگین</h3>
                     <ResponsiveContainer width="100%" height={250}>
                       <BarChart 
                         data={selectedMetric ? [
                           { name: 'شما', value: selectedMetric.نمره ?? 0 },
                           { name: 'میانگین', value: selectedMetric.average ?? 0 } 
                         ] : []}
                         margin={{ top: 20, right: 20, bottom: 10, left: 40 }}
                       >
                         <XAxis dataKey="name" axisLine={false} tickLine={false} />
                         <YAxis domain={[0, 100]} />
                         <Tooltip />
                         <Bar dataKey="value" fill="#1D4ED8" radius={[4, 4, 0, 0]} barSize={24}>
                           <LabelList dataKey="value" position="top" offset={6} style={{ fill: '#000', fontSize: 12 }} />
                         </Bar>
                       </BarChart>
                     </ResponsiveContainer>
                   </div>
 
                   <div className="mt-4">
                     <button 
                       onClick={() => setSelectedMetric(null)} 
                       className="inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-800 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                     >
                       بستن
                     </button>
                   </div>
                 </Dialog.Panel>
               </Transition.Child>
             </div>
           </div>
         </Dialog>
       </Transition>

      {/* Routine Request CTA */}
      <div className="mt-8 px-4 max-w-lg mx-auto text-center">
        <button
          onClick={() => navigate('/routine')}
          className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-lg"
        >
          ثبت درخواست روتین مراقبت از پوست
        </button>
      </div>

      {/* Bottom Navigation */}
      <BottomNavigation />
    </div>
  );
};

export default SkinAnalysisPage; 